import React, { useState, useRef } from 'react';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import BlackoutAPI from '../lib/BlackoutAPI';
import Globals from '../contexts/Globals';
import I18nLib from '../lib/I18nLib';
import { Link, useParams } from 'react-router-dom';
import { logError } from '../lib/Debug';
import passwordResetError from '../lib/PasswordResetError';

import LoaderButton from './LoaderButton';
import Panel from './Panel';

function ResetPasswordForm() {
	
	let globals = Globals.useContainer();
	let i18n = I18nLib.init(globals);
	let { emailParam } = useParams();
	if(!emailParam) emailParam = '';
	
	let [resetSuccessful,setResetSuccessful] = useState(false);
	let [email,setEmail] = useState(emailParam);
	let [resetError,setResetError] = useState(null);
	let [isResetting,setIsResetting] = useState(false);
	const pending = useRef(false);
	const { executeRecaptcha } = useGoogleReCaptcha();
	
	// updates input states
	const handleChange = (e) => {
		setEmail(e.target.value);
		return;
	};

	const handleFocus = () => {
		setResetError(null);
	};

	const handleSubmit = async () => {
		if(pending.current) return;
		pending.current = true;
		setIsResetting(true);
		setResetError(null);
		let stage = 'captcha';
		try {
			if(!executeRecaptcha) throw new Error('captchaUnavailable');
			const captchaToken = await executeRecaptcha('reset_password');
			if(!captchaToken) throw new Error('captchaUnavailable');
			stage = 'request';
			await BlackoutAPI.post({
				endpoint: 'reset-password',
				headers: {
					'Content-Type': 'application/vnd.api+json',
				},
				data:{
					type:'reset-password',
					attributes: {
						Email: email.trim().toLowerCase(),
						CaptchaToken: captchaToken,
					}
				}
			});
			setResetSuccessful(true);
		} catch(err) {
			const failure = passwordResetError(err, stage === 'captcha' ? 'captchaUnavailable' : null);
			setResetError(failure);
			// Fixed fields only: no email, CAPTCHA token or raw provider error.
			logError({ endpoint: 'reset-password', status: failure.status, code: failure.code, errorReference: failure.reference });
		} finally {
			pending.current = false;
			setIsResetting(false);
		}
	};
	
	const handleKeyDown = (e) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			handleSubmit();
		}
	};

	return (
		<Panel>
			<div className="sub-panel center-text">
				{resetSuccessful ? 
					<div>
						<h3>Password Reset</h3>
						<p className="blurb">{i18n('misc.check-email','Check your email for temporary password.')}</p>
						<div className="blurb">{i18n('misc.check-spam','If you do not see it in your main inbox, please check your spam folder.')}</div>
					</div>
					: 
					<form onSubmit={e=>e.preventDefault()}>
						<h2>{i18n('misc.forgot-password','Forgot Password?')}</h2>
						<div className="input-container gap-after">
							<input 
								id='email'
								type='text'
								onChange={handleChange}
								onKeyDown={handleKeyDown}
								defaultValue={email}
								onFocus={handleFocus}
								placeholder='*Email'
							/>
							{resetError ? <span className="validation-text" role="alert"><span className="icon-attention" />
								{resetError.message} ({resetError.status ? `HTTP ${resetError.status}, ` : ''}{resetError.code})
								<br />{resetError.browserReference ? 'Browser reference:' : 'Error reference:'} {resetError.reference}
							</span>
								: ''}
						</div>
						<LoaderButton
							onClick={()=>{handleSubmit();}}
							className="loader-button gap-after"
							isLoading={isResetting}
						>
							<span className="no-wrap">{i18n('misc.reset-password','Reset Password')}</span>
						</LoaderButton>
					</form>
				}
				<div className="gap-before sm no-gap-after">
					<Link to={`/login`} className="upper-link">{i18n('misc.return-to-login','Return to login')}</Link>
				</div>
			</div>
		</Panel>
	);
	
}

function ResetPassword() {
	return <GoogleReCaptchaProvider reCaptchaKey="6LdMSfcjAAAAALeDu1vm1JI-8gRZBx5jY-H6h3Y8">
		<ResetPasswordForm />
	</GoogleReCaptchaProvider>;
}

export default ResetPassword;
